import React from "react";
import { TextField } from "@mui/material";
import {
  landingCopyConstraint,
  landingCopyFieldState,
  landingCopyHelperText,
} from "../copyLimits";
import { registryFieldError } from "./inspectorModel";

export default function LandingCopyTextField({
  field,
  path: explicitPath,
  form,
  baselineForm,
  value,
  onCommit,
  commitMode = "blur",
  trimOnCommit = false,
  helperText = "",
  onKeyDown,
  ...props
}) {
  const path = explicitPath || field?.path || "";
  const current = String(value ?? "");
  const [draft, setDraft] = React.useState(current);
  const constraint = landingCopyConstraint(path);

  React.useEffect(() => {
    setDraft(current);
  }, [current, path]);

  const commit = React.useCallback((rawValue = draft) => {
    const nextValue = trimOnCommit ? String(rawValue ?? "").trim() : String(rawValue ?? "");
    if (constraint) {
      if (nextValue !== current) onCommit?.(nextValue);
      return;
    }
    const error = field ? registryFieldError(field, nextValue) : "";
    if (!error && nextValue !== current) onCommit?.(nextValue);
  }, [constraint, current, draft, field, onCommit, trimOnCommit]);

  const state = constraint
    ? landingCopyFieldState({ path, value: draft, form, baselineForm })
    : null;
  const fallbackError = !constraint && field ? registryFieldError(field, draft) : "";
  const resolvedHelper = state
    ? landingCopyHelperText(state, helperText)
    : fallbackError || helperText;

  return (
    <TextField
      {...props}
      value={draft}
      error={state?.invalid === true || Boolean(fallbackError)}
      helperText={resolvedHelper || undefined}
      inputProps={{
        ...(props.inputProps || {}),
        ...(!constraint && Number.isFinite(field?.maxLength) ? { maxLength: field.maxLength } : {}),
      }}
      FormHelperTextProps={{
        ...(props.FormHelperTextProps || {}),
        ...(state?.legacyUnchanged ? { sx: { color: "warning.main", ...(props.FormHelperTextProps?.sx || {}) } } : {}),
      }}
      onChange={(event) => {
        const nextValue = event.target.value;
        setDraft(nextValue);
        props.onChange?.(event);
        if (commitMode === "change") onCommit?.(nextValue);
      }}
      onBlur={(event) => {
        if (commitMode !== "change") commit(event.target.value);
        props.onBlur?.(event);
      }}
      onKeyDown={(event) => {
        if (onKeyDown) onKeyDown(event);
        if (event.defaultPrevented) return;
        if (commitMode === "blur" && event.key === "Enter" && !props.multiline && !event.nativeEvent?.isComposing) {
          event.preventDefault();
          event.currentTarget.blur();
        }
      }}
    />
  );
}
